import { Component, Input, OnDestroy, Optional } from '@angular/core';
import { AccountingPlanComponent } from 'components/accounting/accounting-plan/accounting-plan.component';
import { ChangeAccountingPlanEndDateComponent } from 'components/accounting/accounting-plan/change-accounting-plan-end-date.component';
import { EditProductPlanNameComponent } from 'components/accounting/product-plan/edit-product-plan-name.component';
import { SelectProductPlanComponent } from 'components/accounting/product-plan/select-product-plan.component';
import { EditProductPlanTerminationNoticePeriodComponent } from 'components/accounting/product-plan/edit-product-plan-termination-notice-period.component';
import { SearchLogComponent, SystemLogFilter } from 'components/admin/system-log/search-log.component';
import { CompanyProductsComponent } from 'components/company/company-products.component';
import { ListColumnComponent, ListHeaderComponent, ListItemComponent, ListRowComponent, ListViewComponent, ListViewDirective } from 'components/layout/list-view/list-view.component';
import { SettlementComponent } from 'components/settlement/settlement.component';
import { UnsuspendAccountingPlanConfirmComponent } from './unsuspend-accounting-plan-confirm.component';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { AccountingPlansLedgerPostsAmounts } from 'models/AccountingPlansLedgerPostsAmounts';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { SettlementModel } from 'models/generated/Entities.GormCrm.Settlement';
import { LogRequestModel } from 'models/generated/Logging.Api.Models.LogRequest';
import { Layout } from 'modules/layout';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { FileStorageService } from 'services/file-storage.service';
import { NavigationService } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { CompanyActiveSettlement } from 'services/settlement.service';
import { ProductPlanService } from 'services/productplan.service';
import { DateService } from 'services/date.service';
import { BillingPlanDtoModel } from 'models/generated/Accounting.Api.Models.BillingPlanDto';

@Component({
	selector: 'product-plan',
	templateUrl: './product-plan.component.html',
	imports: [Layout, ListViewComponent, ListItemComponent, AccountingPlanComponent, ListHeaderComponent, ListViewDirective, ListRowComponent, ListColumnComponent, CurrencyPipe, TranslatePipe, CustomDatePipe, ProductNamePipe]
})
export class ProductPlanComponent implements OnDestroy {

	private _productPlan!: ProductPlanModel;
	private changeSubscription?: Subscription;
	private dataProvider: CompanyDataProvider;

	inputType = InputType;
	credentialTypeEnum = CredentialType;
	replacementsOldPlans: ProductPlanModel[] = [];
	replacementsNewPlans: ProductPlanModel[] = [];
	settlementInfo?: CompanyActiveSettlement;

	@Input() accountingPlan?: AccountingPlanModel;

	@Input()
	set productPlan(plan: ProductPlanModel) {
		this._productPlan = plan;
		this.getPlanReplacements();
	}
	get productPlan() {
		return this._productPlan;
	}

	infinity = Infinity;

	constructor(public readonly accountingService: AccountingService,
		private readonly notificationService: NotificationService,
		private readonly navigation: NavigationService,
		private readonly productPlanService: ProductPlanService,
		dataProviderFactory: CompanyDataProviderFactory,
		private readonly dateService: DateService,
		private readonly fileStorageService: FileStorageService,
		@Optional() public parent?: CompanyProductsComponent) {
		this.dataProvider = dataProviderFactory.getProviderForCurrentScope();
		this.dataProvider.getSettlement().then(s => this.settlementInfo = s);
	}

	selectParentPlan() {
		this.dataProvider.getAllProductPlansAndAccountingPlans().then((plans) => {
			let availableParents = plans.filter(p => this.productPlanService.planIsNotChildOf(p, this.productPlan?.id!));
			this.navigation.openPopup(SelectProductPlanComponent, 'company.product-plans.select-parent.popup-header').then(view => {
				view.headline = 'company.product-plans.edit-product-plan-parent-header';
				view.label = 'company.product-plans.parent';
				view.buttonText = 'general.save';
				view.productPlans = availableParents;
				view.selectedProductPlanId = this.productPlan.parentId;
				view.productPlanSelected.subscribe(selectedPlanId => {
					this.productPlan.parentId = selectedPlanId;
					this.productPlanService.updateProductPlanParent(this.productPlan.id!, selectedPlanId).then(() => { this.navigation.closeView(view); });
				})
			});
		});
	}

	downloadTransferDeclaration() {
		const fileReference = this.getLastTransfer()?.fileReference;
		if (fileReference)
			this.fileStorageService.downloadFile(fileReference.filePath);
	}

	getLastTransfer() {
		const transferPlans = this.productPlan?.transferPlans;
		return transferPlans?.sort((a, b) => new Date(b.transfer!.date).getTime() - new Date(a.transfer!.date).getTime())[0]?.transfer;
	}

	getTransferDeclarationFileName() {
		return this.getLastTransfer()?.fileRereference?.fileName;
	}

	async getPlanReplacements() {
		this.replacementsOldPlans = [];
		this.replacementsNewPlans = [];
		const planReplacements = await this.dataProvider.getPlanReplacements();
		planReplacements.forEach(rep => {
			if (rep.planReplacementNewPlans?.find(s => s.productPlanId == this.productPlan.id)) this.addReplacements(this.replacementsOldPlans!, rep.planReplacementOldPlans);
			if (rep.planReplacementOldPlans?.find(s => s.productPlanId == this.productPlan.id)) this.addReplacements(this.replacementsNewPlans!, rep.planReplacementNewPlans);
		});
	}
	private addReplacements(planList: ProductPlanModel[], replacements: { productPlanId?: string }[]) {
		for (let planId of replacements.map(r => r.productPlanId!)) {
			this.dataProvider.getProductPlanWithAccountingPlans(planId).then(plan => planList.push(plan));
		}
	}

	changeEndDate() {
		this.navigation.openPopup(ChangeAccountingPlanEndDateComponent, 'company.accounting-plans.change-end-date.popup-header').then(view => {
			view.accountingPlan = this.accountingPlan;
			view.productPlan = this.productPlan;
		});
	}

	changeTerminationNoticePeriod() {
		this.navigation.openPopup(EditProductPlanTerminationNoticePeriodComponent, 'company.product-plans.edit-termination-notice-period.popup-header').then(view => {
			view.productPlan = this.productPlan;
		});
	}

	changeProductPlanName() {
		this.navigation.openPopup(EditProductPlanNameComponent, 'company.product-plans.context-menu.change-name.pop-up-title')
			.then(view => view.productPlan = this.productPlan);
	}

	selectProductPlan(productPlanId: string) {
		this.parent?.selectProductPlan(productPlanId);
	}

	selectAccountingPlan(plan: AccountingPlanModel) {
		this.parent?.selectAccountingPlan(plan);
	}

	setIgnorePriceAdjustment(ignore: boolean) {
		var language = ignore ? 'company.accounting-plans.suspend-price-adjustment.confirm' : 'company.accounting-plans.resume-price-adjustment.confirm';
		this.notificationService.confirm(language).subscribe(result => {
			if (result) {
				this.accountingPlan!.ignorePriceAdjustments = ignore;
				this.accountingService.setAccountingPlanPriceAdjustment(this.accountingPlan!, ignore);
			}
		});
	}

	planIsInSettlement(): boolean {
		return this.settlementInfo?.activeSettlement != undefined && this.accountingPlan != undefined && this.settlementInfo.activeSettlement.settlementProductPlans.find(spp => spp.productPlanId == this.accountingPlan?.productPlanId) != undefined;
	}

	async resumeRegistrations() {
		if (await this.dataProvider.planIsPartOfActiveSettlement(this.productPlan)) {
			this.notificationService.warning('company.accounting-plans.warning-plan-is-part-of-settlement');
			return;
		}
		if (await this.dataProvider.planIsGoingToBeReplaced(this.productPlan)) {
			this.notificationService.warning('company.accounting-plans.warning.plan-is-part-of-replacement');
			return;
		}

		this.dataProvider.getLedgerPostsByProductPlanIds([this.productPlan.id!]).then(ledgerPosts => {
			const unissuedLedgerPosts = ledgerPosts.filter(ledgerPost => !ledgerPost.isRegistered);

			if (unissuedLedgerPosts.length > 0 && this.accountingPlan!.endDate! < this.dateService.today()) {
				this.navigation.openPopup(UnsuspendAccountingPlanConfirmComponent, 'company.accounting-plans.resume-and-delete-unissued-invoices-confirmation.popup-header').then(view => {
					view.confirmEvent.subscribe((deleteLedgerPosts) => {
						if (deleteLedgerPosts) {
							let billingPlan = new BillingPlanDtoModel();
							const newExpectedTotalValue = this.accountingPlan!.totalPlanValue - unissuedLedgerPosts.reduce((sum: number, lp: { value: number }) => sum + lp.value, 0);
							billingPlan.accountingPlanId = this.accountingPlan!.id;
							billingPlan.expectedTotalValue = newExpectedTotalValue;
							this.accountingService.unsuspend(this.productPlan.id!, billingPlan);
						} else {
							this.accountingService.unsuspend(this.productPlan.id!);
						}
						this.navigation.closeView(view);
					});
				});
			} else {
				this.notificationService.confirm('company.accounting-plans.resume-confirmation-text', { 'productPlanName': this.productPlan.productName }).subscribe(result => {
					if (result)
						this.accountingService.unsuspend(this.productPlan.id!);
				});
			}
		});
	}

	async showSystemLog() {
		let logFilter: SystemLogFilter[] = [];

		if (this.productPlan.accountingPlans)
			logFilter.push(new SystemLogFilter(AccountingPlanModel, { entityIds: this.productPlan.accountingPlans.map((plan: AccountingPlanModel) => plan.id!) } as LogRequestModel));
		if (this.productPlan?.id)
			logFilter.push(new SystemLogFilter(ProductPlanModel, { entityIds: [this.productPlan.id!] } as LogRequestModel));

		if (logFilter.length == 0) return;

		const view = await this.navigation.openPopup(SearchLogComponent);
		view.filteredEntities = logFilter;
	}

	isDeletable() {
		if (this.productPlan.deliveryDate || this.productPlan.autoRenewalDate) return false;
		if (this.accountingPlan?.startDate || this.accountingPlan?.endDate) return false;
		if (this.accountingPlan?.startUpDeliveryDate) return false;
		if (this.productPlan.accountingPlans?.length !== 1) return false;
		if (this.isSuspended()) return false;
		if (this.productPlan.orderLines.length > 0) return false;

		return true;
	}

	deleteAccountingPlan() {
		this.notificationService.confirm('company.product-plans.confirm-delete', { 'productPlanName': this.productPlan.productName }).subscribe(result => {
			if (result)
				this.accountingService.deleteAccountingPlan(this.accountingPlan!.id!);
		});
	}

	openSettlement() {
		if (this.settlementInfo?.activeSettlement) {
			this.navigation.openPopup(CompanySettlementComponent, 'company.open-settlement.popup-header').then(view => {
				view.settlement = this.settlementInfo!.activeSettlement;
			});
		}
	}

	isActive() {
		if (!this.accountingPlan) return false;
		return this.accountingPlan!.endDate && this.accountingPlan!.endDate >= new Date(Date.now());
	}

	isSuspended() {
		return this.accountingPlan!.suspended != undefined;
	}

	ngOnDestroy(): void {
		if (this.changeSubscription) this.changeSubscription.unsubscribe();
	}
}

@Component({
	selector: 'company-settlement',
	template: '@if (settlement) {<panel width="600"><settlement [settlement]=settlement [plansAmount]="plansAmount"></settlement></panel>}',
	imports: [Layout, SettlementComponent]
})
export class CompanySettlementComponent {

	plansAmount = new AccountingPlansLedgerPostsAmounts();

	private _settlement?: SettlementModel;
	@Input() set settlement(settlement: SettlementModel | undefined) {

		if (settlement) {
			let productPlanIds = settlement.settlementProductPlans.map(spp => spp.productPlanId!);
			this.plansAmount = new AccountingPlansLedgerPostsAmounts();
			this.accountingService.getPlansOpenAndClosedAmounts(productPlanIds).then(amounts => {
				this.plansAmount = amounts;
			})
		}
		this._settlement = settlement;
	}
	get settlement() { return this._settlement; }

	constructor(private readonly accountingService: AccountingService) {

	}
}
